import { defineCollection } from "@rebasepro/common";

/**
 * One row per model call — what it was for, what it cost, and whether it ran.
 *
 * ── Why a row per call and not a daily counter ───────────────────────────────
 * A counter answers "how much did last night cost", which is the question you
 * ask once. A ledger answers "which route got expensive, and when did it
 * change", which is the question you ask when the bill moves. Nightly volume is
 * tens of calls, not thousands, so the cheap thing and the useful thing are the
 * same thing here.
 *
 * It is also the budget's own memory. The cap is enforced against a SUM over
 * today's rows rather than a number held in the process, because the pod
 * restarts and a budget that forgets a restart is not a budget.
 *
 * `cost_usd` is an ESTIMATE — our own price table applied to the token counts
 * the API reported. It is not a bill, it does not know about free tiers or
 * committed-use discounts, and a model with no entry in the table records
 * `null` rather than a made-up number.
 */
const modelSpendCollection = defineCollection({
    name: "Model spend",
    singularName: "Model call",
    slug: "model_spend",
    table: "model_spend",
    description: "Every model call this app made, with an estimated cost. The budget is enforced against this table.",
    properties: {
        id: { name: "ID", type: "string", isId: "uuid", admin: { readOnly: true } },

        job: {
            name: "Job",
            type: "string",
            validation: { required: true },
            description: "The cron that was running — source-leads, draft-outreach, …"
        },
        route: {
            name: "Route",
            type: "string",
            validation: { required: true },
            enum: [
                { id: "assess", label: "Score leads", color: "blue" },
                { id: "draft", label: "Draft outreach", color: "green" },
                { id: "discover", label: "Discover companies", color: "purple" },
                { id: "brief", label: "Morning brief", color: "gray" },
                { id: "visibility", label: "AI visibility", color: "cyan" },
                { id: "triage", label: "Screen the inbox", color: "orange" }
            ],
            description: "Which lane the spend belongs to. Budgets and shedding are per route. Every route in backend/src/agent/model.ts needs a value here: Postgres rejects an unknown one, and the call goes unrecorded and uncapped."
        },
        model: { name: "Model", type: "string" },

        outcome: {
            name: "Outcome",
            type: "string",
            validation: { required: true },
            defaultValue: "ok",
            enum: [
                { id: "ok", label: "Called", color: "green" },
                { id: "failed", label: "Call failed", color: "red" },
                { id: "shed", label: "Shed (budget)", color: "orange" },
                { id: "breaker", label: "Refused (breaker open)", color: "yellow" }
            ],
            description: "A refused call is recorded too — a night that produced nothing because it was shed must not look like a night nobody asked for anything."
        },

        input_tokens: { name: "Input tokens", type: "number" },
        output_tokens: {
            name: "Output tokens",
            type: "number",
            description: "Answer plus thinking tokens — both are billed at the output rate"
        },
        cached_tokens: { name: "Cached input tokens", type: "number" },
        cost_usd: {
            name: "Est. cost (USD)",
            type: "number",
            description: "Estimated from our price table. Null when the model has no entry — never guessed."
        },
        duration_ms: { name: "Duration (ms)", type: "number" },
        error: { name: "Error", type: "string", admin: { multiline: true } },

        called_at: {
            name: "Called at",
            type: "date",
            autoValue: "on_create",
            admin: { readOnly: true }
        }
    },
    admin: {
        icon: "Paid",
        group: "Diagnostics",
        defaultViewMode: "table",
        display: {
            title: "route"
        },
        sort: ["called_at", "desc"],
        propertiesOrder: [
            "called_at",
            "job",
            "route",
            "outcome",
            "cost_usd",
            "input_tokens",
            "output_tokens",
            "cached_tokens",
            "duration_ms",
            "model",
            "error"
        ]
    }
});

export default modelSpendCollection;
